import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ScrollToTop from "@/components/ScrollToTop";

import PublicLayout from "@/components/PublicLayout";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";

import SecretariatGeneral from "@/pages/services/SecretariatGeneral";
import Bibliotheque from "@/pages/services/Bibliotheque";
import BibliothequesNumeriques from "@/pages/services/BibliothequesNumeriques";
import Musees from "@/pages/services/Musees";
import MuseumSection from "@/pages/services/MuseumSection";
import EcoleDoctorale from "@/pages/services/EcoleDoctorale";
import Cecom from "@/pages/services/Cecom";

import Directions from "@/pages/directions/Directions";
import DirectionBibliothequesMusees from "@/pages/directions/DirectionBibliothequesMusees";
import DirectionSuiviEncadrement from "@/pages/directions/DirectionSuiviEncadrement";
import Pusic from "@/pages/directions/Pusic";
import PusicPublications from "@/pages/directions/PusicPublications";
import UniteReproduction from "@/pages/directions/UniteReproduction";
import Formation from "@/pages/directions/Formation";
import BibliothequesMuseesCentrale from "@/pages/directions/BibliothequesMuseesCentrale";
import BibliothequeCentrale from "@/pages/directions/BibliothequeCentrale";
import ActivitesAnimation from "@/pages/directions/ActivitesAnimation";
import MuseesUniversitairesHub from "@/pages/directions/MuseesUniversitairesHub";
import MuseumThematique from "@/pages/directions/MuseumThematique";
import LaboratoireCecom from "@/pages/directions/LaboratoireCecom";

import DepotsHome from "@/pages/platforms/DepotsHome";
import FormulaireMemoire from "@/pages/platforms/FormulaireMemoire";
import FormulaireProjet from "@/pages/platforms/FormulaireProjet";
import ArchivesAcademiques from "@/pages/platforms/ArchivesAcademiques";
import Revues from "@/pages/platforms/Revues";
import SuiviDepot from "@/pages/platforms/SuiviDepot";
import ActionConfirm from "@/pages/ActionConfirm";

import { NewsList, NewsDetail } from "@/pages/News";

import DashboardLayout from "@/components/DashboardLayout";
import DashboardHome from "@/pages/dashboard/DashboardHome";
import DepositsList from "@/pages/dashboard/DepositsList";
import DepositDetail from "@/pages/dashboard/DepositDetail";
import AcademicYears from "@/pages/dashboard/AcademicYears";
import AdminLists from "@/pages/dashboard/AdminLists";
import AdminUsers from "@/pages/dashboard/AdminUsers";
import AdminNews from "@/pages/dashboard/AdminNews";
import LibraryDashboard from "@/pages/dashboard/LibraryDashboard";
import PublicationsAdmin from "@/pages/dashboard/PublicationsAdmin";
import MuseumAdmin from "@/pages/dashboard/MuseumAdmin";
import SubscriptionRequests from "@/pages/dashboard/SubscriptionRequests";
import ContentEditor from "@/pages/dashboard/ContentEditor";
import SiteSettings from "@/pages/dashboard/SiteSettings";
import StudentCodes from "@/pages/dashboard/StudentCodes";

function RequireRole({ roles, children }) {
  const { user } = useAuth();
  if (user === undefined) return <div className="p-10 text-slate-500">Chargement...</div>;
  if (!user) return <Navigate to="/connexion" replace />;
  if (user.role !== "super_admin" && !roles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Toaster position="top-right" />
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />

            {/* Nouvelles Directions (niveau 1) */}
            <Route path="/directions" element={<Directions />} />
            <Route path="/directions/bibliotheques-musees" element={<DirectionBibliothequesMusees />} />
            <Route path="/directions/ecole-doctorale" element={<EcoleDoctorale />} />
            <Route path="/directions/suivi-encadrement" element={<DirectionSuiviEncadrement />} />
            {/* Sous-structures de la Direction Bibliothèques et Musées */}
            <Route path="/directions/bibliotheques-musees/cecom" element={<Cecom />} />
            <Route path="/directions/bibliotheques-musees/cecom/laboratoires/:code" element={<LaboratoireCecom />} />
            <Route path="/directions/bibliotheques-musees/pusic" element={<Pusic />} />
            <Route path="/directions/bibliotheques-musees/pusic/publications" element={<PusicPublications />} />
            <Route path="/directions/bibliotheques-musees/pusic/reproduction" element={<UniteReproduction />} />
            <Route path="/directions/bibliotheques-musees/formation" element={<Formation />} />
            <Route path="/directions/bibliotheques-musees/bibliotheques-musees" element={<BibliothequesMuseesCentrale />} />
            <Route path="/directions/bibliotheques-musees/bibliotheques-musees/bibliotheque-centrale" element={<BibliothequeCentrale />} />
            <Route path="/directions/bibliotheques-musees/bibliotheques-musees/bibliotheque-numerique" element={<BibliothequesNumeriques />} />
            <Route path="/directions/bibliotheques-musees/bibliotheques-musees/activites-animation" element={<ActivitesAnimation />} />
            <Route path="/directions/bibliotheques-musees/bibliotheques-musees/musees" element={<MuseesUniversitairesHub />} />
            <Route path="/directions/bibliotheques-musees/bibliotheques-musees/musees/phototheque" element={<MuseumThematique section="phototheque" />} />
            <Route path="/directions/bibliotheques-musees/bibliotheques-musees/musees/presse-ecrite" element={<MuseumThematique section="presse_ecrite" />} />
            <Route path="/directions/bibliotheques-musees/bibliotheques-musees/musees/audiovisuel" element={<MuseumThematique section="audiovisuel" />} />
            <Route path="/directions/bibliotheques-musees/bibliotheques-musees/musees/cinema" element={<MuseumThematique section="cinema" />} />
            <Route path="/directions/bibliotheques-musees/bibliotheques-musees/musees/tic" element={<MuseumThematique section="tic" />} />
            {/* Ancien parcours Bibliothèque / Musées (compat rétro) */}
            <Route path="/directions/bibliotheques-musees/bibliotheques-numeriques" element={<BibliothequesNumeriques />} />
            <Route path="/directions/bibliotheques-musees/musees" element={<Musees />} />
            <Route path="/directions/bibliotheques-musees/musees/archives-photos" element={<MuseumSection section="photos" />} />
            <Route path="/directions/bibliotheques-musees/musees/archives-publications" element={<MuseumSection section="publications" />} />
            <Route path="/directions/bibliotheques-musees/musees/archives-evenements" element={<MuseumSection section="evenements" />} />

            {/* Compatibilité rétro — anciennes URL /services/* → nouvelles /directions/* */}
            <Route path="/services" element={<Navigate to="/directions" replace />} />
            <Route path="/services/secretariat-general" element={<SecretariatGeneral />} />
            <Route path="/services/bibliotheque" element={<Navigate to="/directions/bibliotheques-musees" replace />} />
            <Route path="/services/bibliotheques-numeriques" element={<Navigate to="/directions/bibliotheques-musees/bibliotheques-numeriques" replace />} />
            <Route path="/services/musees" element={<Navigate to="/directions/bibliotheques-musees/musees" replace />} />
            <Route path="/services/musees/archives-photos" element={<Navigate to="/directions/bibliotheques-musees/musees/archives-photos" replace />} />
            <Route path="/services/musees/archives-publications" element={<Navigate to="/directions/bibliotheques-musees/musees/archives-publications" replace />} />
            <Route path="/services/musees/archives-evenements" element={<Navigate to="/directions/bibliotheques-musees/musees/archives-evenements" replace />} />
            <Route path="/services/ecole-doctorale" element={<Navigate to="/directions/ecole-doctorale" replace />} />
            <Route path="/services/cecom" element={<Navigate to="/directions/bibliotheques-musees/cecom" replace />} />

            <Route path="/plateformes/depots" element={<DepotsHome />} />
            <Route path="/plateformes/depots/memoire" element={<FormulaireMemoire />} />
            <Route path="/plateformes/depots/projet-tutore" element={<FormulaireProjet />} />
            <Route path="/plateformes/archives-academiques" element={<ArchivesAcademiques />} />
            <Route path="/plateformes/revues" element={<Revues />} />
            <Route path="/suivi-depot" element={<SuiviDepot />} />
            <Route path="/actualites" element={<NewsList />} />
            <Route path="/actualites/:id" element={<NewsDetail />} />
          </Route>

          <Route path="/connexion" element={<Login />} />
          <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
          <Route path="/reinitialiser-mot-de-passe" element={<ResetPassword />} />
          <Route path="/actions/:token" element={<ActionConfirm />} />

          <Route path="/dashboard" element={<DashboardLayout />}>
            <Route index element={<DashboardHome />} />
            <Route path="depots" element={<RequireRole roles={["deposits_service"]}><DepositsList /></RequireRole>} />
            <Route path="depots/:id" element={<RequireRole roles={["deposits_service"]}><DepositDetail /></RequireRole>} />
            <Route path="codes-etudiants" element={<RequireRole roles={["deposits_service"]}><StudentCodes /></RequireRole>} />
            <Route path="bibliotheque" element={<RequireRole roles={["library"]}><LibraryDashboard /></RequireRole>} />
            <Route path="publications" element={<RequireRole roles={["library"]}><PublicationsAdmin /></RequireRole>} />
            <Route path="musees" element={<RequireRole roles={["library"]}><MuseumAdmin /></RequireRole>} />
            <Route path="abonnements" element={<RequireRole roles={["library"]}><SubscriptionRequests /></RequireRole>} />
            <Route path="contenu" element={<RequireRole roles={[]}><ContentEditor /></RequireRole>} />
            <Route path="reglages" element={<RequireRole roles={[]}><SiteSettings /></RequireRole>} />
            <Route path="actualites" element={<RequireRole roles={[]}><AdminNews /></RequireRole>} />
            <Route path="annees" element={<RequireRole roles={[]}><AcademicYears /></RequireRole>} />
            <Route path="listes" element={<RequireRole roles={[]}><AdminLists /></RequireRole>} />
            <Route path="utilisateurs" element={<RequireRole roles={[]}><AdminUsers /></RequireRole>} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
